import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button, FormGroup, Input } from 'reactstrap';
import { useNotificationUIContext } from '../NotificationUIProvider';
import NotificationTableColumns, { NotificationColumns } from './NotificationTableColumns';
import { NotificationTableSearchBar } from './NotificationTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getNotificationList,
  setNotificationFilters,
  toggleCheckedItemById,
} from '../../redux/actions';
import SanitizeHTML from '@/shared/components/SanitizeHTML';
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';

const NotificationTable = () => {
  const notificationUIContext = useNotificationUIContext();
  const dispatch = useDispatch();
  const {
    data: notifications,
    error,
    filters,
    loading,
    totalRecords,
    checkedNotificationIds,
  } = useSelector((state) => state.cadsAINotifications, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(NotificationColumns.length);
  }, [NotificationColumns.length]);

  React.useEffect(() => {
    dispatch(getNotificationList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setNotificationFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (notificationId) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => notificationUIContext.openEditNotificationDialog(notificationId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setNotificationFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setNotificationFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - notifications.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <NotificationTableSearchBar />
        <Table className="material-table">
          <NotificationTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading notifications...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!notifications.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Notifications'
                    : 'No Notifications with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {notifications.map((notification) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={notification?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={notification?.id}
                        defaultChecked={checkedNotificationIds.includes(notification?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    role="button"
                    onClick={() =>
                      notificationUIContext.openViewNotificationDialog(notification?.id)
                    }
                  >
                    {notification.title}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    role="button"
                    onClick={() =>
                      notificationUIContext.openViewNotificationDialog(notification?.id)
                    }
                  >
                    <SanitizeHTML html={notification.description} />
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    role="button"
                    onClick={() =>
                      notificationUIContext.openViewNotificationDialog(notification?.id)
                    }
                  >
                    <SanitizeHTML html={notification.actions} />
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(notification?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default NotificationTable;
